<template>
  <v-combobox
    v-model="comboboxValue"
    v-maska="maska"
    ref="comboboxRef"
    :loading="loading"
    :label="label"
    :items="items"
    :menu-props="{
      ...menuProps,
      contentClass: [
        'e-combobox__content high-z-index',
        { 'e-combobox__content--error': isError || errorStyle, 'hide-title': slots.item },
      ],
      width: 'auto',
      maxWidth: '100%',
      closeOnContentClick: !!slots.item,
    }"
    :item-title="itemTitle"
    :item-value="itemValue"
    :return-object="returnObject"
    :no-filter="noFilter"
    :class="{
      error: errorStyle,
      hideSelected,
    }"
    :menu-icon="menuIcon"
    :multiple="multiple"
    :error-messages="errorMessages"
    :placeholder="placeholder"
    variant="outlined"
    hide-details="auto"
    hide-no-data
    auto-select-first
    class="e-combobox"
    @update:search="onSearchUpdate"
  >
    <template v-if="slots.selection" #selection="{ item }">
      <slot name="selection" :item="item" />
    </template>
    <template v-if="slots.item" #item="{ item, index, props }">
      <v-list-item v-bind="props" @click="onItemClick(item.raw)">
        <slot name="item" :item="item" :index="index" />
      </v-list-item>
    </template>
    <template v-if="slots['prepend-inner']" #prepend-inner>
      <slot name="prepend-inner" />
    </template>
    <template v-if="slots['append-inner']" #append-inner>
      <slot name="append-inner" />
    </template>
    <template v-if="slots.append" #append>
      <slot name="append" />
    </template>
  </v-combobox>
</template>

<script setup lang="ts" generic="T extends Dictionary<unknown> | string">
import { useVModel } from '@vueuse/core';
import { vMaska } from 'maska/vue';
import { onUpdated, ref, toRef } from 'vue';
import type { EComboboxModelType } from '@/core/types/components/components';
import type { Dictionary } from '@/core/types/gengeric';
import type { VCombobox } from 'vuetify/components';
import { type ListItem } from '@/core/types/components/selection';

const props = defineProps<{
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  modelValue?: any;
  inputValue?: string;
  label?: string;
  items?: T[];
  loading?: boolean;
  itemTitle?: string;
  itemValue?: string;
  returnObject?: boolean;
  errorStyle?: boolean;
  noFilter?: boolean;
  multiple?: boolean;
  hideSelected?: boolean;
  showMenuIcon?: boolean;
  persistentPlaceholder?: boolean;
  placeholder?: string;
  maska?: string;
  errorMessages?: string | string[];
  menuProps?: VCombobox['$props']['menuProps'];
}>();

const slots = defineSlots<{
  item?: (props: { item: ListItem<T>; index: number }) => void;
  selection?: (props: { item: ListItem<T> }) => void;
  'prepend-inner'?: (props: unknown) => void;
  'append-inner'?: (props: unknown) => void;
  append?: (props: unknown) => void;
}>();

const emit = defineEmits<{
  (event: 'update:modelValue', value: EComboboxModelType<T>): void;
  (event: 'update:search', value: string): void;
  (event: 'update:inputValue', value: string): void;
  (event: 'onItemClick', value: EComboboxModelType<T>): void;
}>();

const onItemClick = (value: EComboboxModelType<T>) => {
  emit('onItemClick', value);
};

const comboboxValue = useVModel(props, 'modelValue');
const inputValue = useVModel(props, 'inputValue');

const menuIcon = toRef(() => (props.showMenuIcon ? undefined : ''));

const onSearchUpdate = (searchString: string) => {
  inputValue.value = searchString;

  emit('update:search', searchString);
};

const comboboxRef = ref();

const isError = ref(false);

let timeout = 0;
onUpdated(() => {
  window.clearTimeout(timeout);
  timeout = window.setTimeout(() => {
    isError.value = comboboxRef.value?.$el.classList.contains('v-input--error');
  });
});

defineExpose({
  input: comboboxRef,
});
</script>

<style scoped lang="scss">
.e-combobox {
  position: relative;

  :deep(.v-combobox__selection) {
    height: auto;

    line-height: 1;
  }

  &.hideSelected {
    :deep(.v-combobox__selection) {
      display: none;
    }
  }
}

:global(.e-combobox__content .v-selection-control__wrapper) {
  margin-right: 8px !important;
}
</style>
